import {
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiClockAlertOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";
import ModalDialog from "../../../../ui/new_components/common/modal";
import { po_approval_status } from "../../model/purchase_order_model";

interface ApprovalConfirmModalProps {
  show: boolean;
  approval_status: string;
  level: string;
  isSubmitting?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const ApprovalConfirmModal: FunctionComponent<ApprovalConfirmModalProps> = ({
  show,
  approval_status,
  level,
  isSubmitting = false,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (show) setReason("");
  }, [show, approval_status]);

  const config = (() => {
    switch (approval_status) {
      case po_approval_status.approved:
        return {
          icon: mdiCheckCircleOutline,
          accent: "text-green-600",
          iconBg: "bg-green-50",
          confirmBtn: "bg-green-600 hover:bg-green-700",
          heading: "Approve Purchase Order",
          message: `You're about to approve the ${level} approval for this purchase order. This will move it forward in the approval workflow.`,
          confirmText: "Approve",
          needsReason: false,
        };
      case po_approval_status.rejected:
        return {
          icon: mdiCloseCircleOutline,
          accent: "text-red-600",
          iconBg: "bg-red-50",
          confirmBtn: "bg-red-600 hover:bg-red-700",
          heading: "Reject Purchase Order",
          message: `You're about to reject the ${level} approval. Please share why this purchase order is being rejected.`,
          confirmText: "Reject",
          needsReason: true,
        };
      default:
        return {
          icon: mdiClockAlertOutline,
          accent: "text-amber-600",
          iconBg: "bg-amber-50",
          confirmBtn: "bg-amber-600 hover:bg-amber-700",
          heading: "Mark Approval as Pending",
          message: `You're about to mark the ${level} approval as pending. Please share the reason for keeping it pending.`,
          confirmText: "Mark Pending",
          needsReason: true,
        };
    }
  })();

  const trimmedReason = reason.trim();
  const canConfirm =
    !isSubmitting && (!config.needsReason || trimmedReason.length > 0);

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(trimmedReason);
  };

  return (
    <ModalDialog id="po_approval_confirm_modal" show={show} title="" onClose={onCancel}>
      <div className="p-4 sm:p-5">
        <div className="flex flex-col items-center text-center gap-3">
          <div className={`rounded-full p-3 ${config.iconBg}`}>
            <Icon path={config.icon} size={1.6} className={config.accent} />
          </div>
          <h3 className={`text-lg font-bold ${config.accent}`}>
            {config.heading}
          </h3>
          <p className="text-sm text-gray-600 max-w-sm">{config.message}</p>
        </div>

        {config.needsReason && (
          <div className="mt-4">
            <label className="text-xs font-semibold text-gray-500">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              id="po_approval_confirm_reason"
              autoFocus
              rows={3}
              value={reason}
              disabled={isSubmitting}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter a reason..."
              className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-myPrimaryColor focus:ring-1 focus:ring-myPrimaryColor outline-none resize-none disabled:opacity-50"
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-5 mt-4 border-t border-gray-100">
          <button
            id="po_approval_confirm_cancel_btn"
            onClick={onCancel}
            disabled={isSubmitting}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 transition-all ${
              isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50 hover:border-gray-400"
            }`}
          >
            Cancel
          </button>
          <button
            id="po_approval_confirm_submit_btn"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-bold text-white shadow-sm transition-all active:scale-95 ${
              config.confirmBtn
            } ${!canConfirm ? "cursor-not-allowed opacity-50" : ""}`}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : (
              config.confirmText
            )}
          </button>
        </div>
      </div>
    </ModalDialog>
  );
};

export default ApprovalConfirmModal;
